import { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import { notifier } from 'components/Toaster';
import RefundActionModal from './components/RefundActionModal';
import useRefund from 'hooks/API/GET/useRefund';
import useManageRefund from 'hooks/API/useManageRefund';
import { AppButton } from 'components';
import * as AppPics from 'assets/images';

const STATUS_BADGE = {
  pending: 'badge bg-warning',
  under_review: 'badge bg-primary',
  approved: 'badge bg-success',
  rejected: 'badge bg-danger',
  processed: 'badge bg-info',
};

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const formatCurrency = (amount, currency = 'INR') =>
  `${currency === 'INR' ? '₹' : currency}${Number(amount || 0).toLocaleString('en-IN')}`;

function StatusTimeline({ statusHistory = [] }) {
  if (statusHistory.length === 0) {
    return <p className="text-muted px-4 py-5 font-lg">No status history available.</p>;
  }

  return (
    <div className="px-5 py-5">
      {statusHistory.map((event, idx) => {
        const isLast = idx === statusHistory.length - 1;
        return (
          <div key={idx} className="d-flex position-relative pb-5">
            {/* Success line connector */}
            {!isLast && (
              <div
                className="position-absolute"
                style={{
                  left: '11px',
                  top: '30px',
                  bottom: '0',
                  width: '2px',
                  backgroundColor: '#edf2f7',
                }}
              />
            )}

            {/* Indicator Dot */}
            <div
              className="bg-white d-flex align-items-center justify-content-center"
              style={{
                width: '24px',
                height: '24px',
                minWidth: '24px',
                minHeight: '24px',
                borderRadius: '50%',
                border: `3.5px solid ${isLast ? '#7e1f8c' : '#cbd5e0'}`,
                zIndex: 2,
                marginTop: '4px',
                flex: '0 0 auto',
              }}
            >
              {isLast && (
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#7e1f8c',
                    flex: '0 0 auto',
                  }}
                />
              )}
            </div>

            {/* Content Segment */}
            <div className="flex-grow-1" style={{ marginLeft: '45px' }}>
              <div className="d-flex align-items-center flex-wrap mb-3">
                <span
                  className={STATUS_BADGE[event.status] || 'badge bg-secondary'}
                  style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    padding: '6px 16px',
                    borderRadius: '8px',
                    textTransform: 'capitalize',
                    marginRight: '30px',
                    color: '#fff',
                  }}
                >
                  {(event.status || '').replace(/_/g, ' ')}
                </span>
                <span className="text-muted fw-medium" style={{ fontSize: '1rem' }}>
                  {formatDate(event.timestamp)}
                </span>
              </div>

              {event.note && (
                <p
                  className="mb-2 text-dark"
                  style={{
                    fontSize: '1.2rem',
                    lineHeight: '1.6',
                    fontWeight: 500,
                    color: '#2d3748',
                  }}
                >
                  {event.note}
                </p>
              )}

              {event.changedBy && (
                <div style={{ fontSize: '1rem', color: '#718096' }}>
                  Action by{' '}
                  <span className="fw-bold" style={{ color: '#4a5568' }}>
                    {event.changedBy}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div
      className="card mb-4 shadow-sm border-0"
      style={{ borderRadius: '12px', background: '#fff', border: '1px solid #eee' }}
    >
      <div
        className="card-header bg-white py-4 px-4 border-bottom"
        style={{ borderBottom: '1px solid #f8f9fa' }}
      >
        <h5 className="mb-0 fw-bold" style={{ color: '#7e1f8c', fontSize: '1.4rem' }}>
          {title}
        </h5>
      </div>
      <div className="card-body p-0">
        <div className="list-group list-group-flush">{children}</div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="list-group-item py-4 px-4 border-0 border-bottom">
      <div className="row align-items-center">
        <div className="col-md-4 text-muted fw-medium" style={{ fontSize: '1.2rem' }}>
          {label}
        </div>
        <div className="col-md-8 fw-bold text-dark" style={{ fontSize: '1.4rem' }}>
          {value ?? '—'}
        </div>
      </div>
    </div>
  );
}

function RefundDetailPage() {
  const { refundId } = useParams();
  const history = useHistory();

  // Modal state (approve / reject)
  const [modal, setModal] = useState({ open: false, mode: null });

  const refundQuery = useRefund({ enabled: true, refundId });
  const { isLoading: actionLoading } = useManageRefund();

  // Response shape from the detail endpoint:
  // axios wraps body in .data → { data: { refund, previousRefunds } }
  const refundData = refundQuery.data?.data?.data || null;

  const isLoading = refundQuery.isLoading;
  const isError = refundQuery.isError;

  if (isLoading) {
    return (
      <div className="mainWraper coursetabsection">
        <div className="tab">
          <div className="tab__content ___adjust___tab___padding">
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: '300px' }}
            >
              <span className="spinner-border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !refundData) {
    const errMsg = refundQuery.error?.response?.data?.message || 'Failed to load refund details.';
    return (
      <div className="mainWraper coursetabsection">
        <div className="tab">
          <div className="tab__content ___adjust___tab___padding">
            <div className="px-4 py-4">
              <div className="alert alert-danger">{errMsg}</div>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => history.push('/manage/refunds')}
              >
                ← Back to Refunds
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { refund, previousRefunds = [] } = refundData;
  const user = refund.userId || {};
  const tx = refund.transactionId || {};
  // Admin can approve/reject while the refund is still pending or under_review.
  // Approval now triggers an automatic gateway refund — no separate 'mark as processed' step.
  const canAct = ['pending', 'under_review'].includes(refund.status);

  return (
    <>
      {/* Approve / Reject modal */}
      {modal.open && (
        <RefundActionModal
          mode={modal.mode}
          refund={refund}
          onClose={() => setModal({ open: false, mode: null })}
          onSuccess={() => {
            setModal({ open: false, mode: null });
          }}
        />
      )}

      <div className="mainWraper coursetabsection">
        <div className="tab">
          <ul className="tab__menu">
            <li className="back">
              <button className="btn-back" onClick={() => history.push('/manage/refunds')}>
                <img src={AppPics.BackSvg} alt="Back" />
              </button>
            </li>
          </ul>
          <div className="tab__content ___adjust___tab___padding">
            {/* Breadcrumb */}
            <div className="tab__content-actHead">
              <AppBreadcrumbs
                links={[
                  { label: 'Dashboard', to: '/console' },
                  { label: 'Refunds', to: '/manage/refunds' },
                ]}
                current={`Refund #${String(refund._id).slice(-6).toUpperCase()}`}
              />
            </div>

            <div className="tab__content-actBody">
              <div className="px-5 py-5">
                {/* Page header */}
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3 border-bottom pb-4">
                  <div className="d-flex align-items-center gap-4">
                    <h2 className="prime-head mb-0" style={{ fontSize: '2.2rem', fontWeight: 800 }}>
                      Refund #{String(refund._id).slice(-6).toUpperCase()}
                    </h2>
                    <span
                      className={STATUS_BADGE[refund.status] || 'badge bg-secondary'}
                      style={{
                        fontSize: '1.1rem',
                        padding: '6px 20px',
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        borderRadius: '30px',
                        display: 'inline-block',
                        marginLeft: '25px',
                        color: '#fff',
                      }}
                    >
                      {(refund.status || '').replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-8">
                    {/* 1. User & Course Info */}
                    <InfoCard title="User & Course Information">
                      <div className="list-group-item py-5 px-4 border-0 border-bottom">
                        <div className="d-flex align-items-center gap-4">
                          <img
                            src={user.avatarFullUrl || '/static/media/avatar.svg'}
                            alt={user.name || 'User'}
                            className="rounded-circle shadow-sm"
                            style={{
                              width: '72px',
                              height: '72px',
                              objectFit: 'cover',
                              border: '3px solid #7e1f8c',
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <div>
                            <p
                              className="mb-1 fw-bold"
                              style={{ fontSize: '1.6rem', color: '#1a1a1a' }}
                            >
                              {user.name || '—'}
                            </p>
                            <p className="mb-0 text-muted" style={{ fontSize: '1.2rem' }}>
                              {user.email || '—'}
                            </p>
                          </div>
                        </div>
                      </div>
                      <InfoRow label="Course" value={refund.courseName} />
                      <InfoRow
                        label="Amount"
                        value={formatCurrency(refund.amount, refund.currency)}
                      />
                      <InfoRow label="Refund Method" value={refund.refundMethod || '—'} />
                      {tx.paymentMode && <InfoRow label="Payment Mode" value={tx.paymentMode} />}
                      {tx._id && (
                        <InfoRow
                          label="Transaction ID"
                          value={
                            <code
                              className="text-primary p-2 rounded"
                              style={{ fontSize: '1.2rem', background: '#f8f9fa' }}
                            >
                              {tx._id}
                            </code>
                          }
                        />
                      )}
                      {tx.createdAt && (
                        <InfoRow label="Purchase Date" value={formatDate(tx.createdAt)} />
                      )}
                    </InfoCard>

                    {/* 2. Refund Info */}
                    <InfoCard title="Refund Information">
                      <InfoRow
                        label="Reason"
                        value={
                          <span style={{ textTransform: 'capitalize' }}>
                            {(refund.reason || '').replace(/_/g, ' ')}
                          </span>
                        }
                      />
                      {refund.details && <InfoRow label="Details" value={refund.details} />}
                      <InfoRow label="Submitted At" value={formatDate(refund.submittedAt)} />
                      {refund.expectedCreditDate && (
                        <InfoRow
                          label="Processed At"
                          value={formatDate(refund.expectedCreditDate)}
                        />
                      )}
                      {refund.gatewayRefundId && (
                        <InfoRow
                          label="Gateway Ref"
                          value={
                            <code
                              className="text-primary p-2 rounded"
                              style={{ fontSize: '1.2rem', background: '#f8f9fa' }}
                            >
                              {refund.gatewayRefundId}
                            </code>
                          }
                        />
                      )}
                    </InfoCard>

                    {/* 3. Previous Refunds */}
                    {previousRefunds.length > 0 && (
                      <InfoCard title="Previous Refund Requests from this User">
                        <div className="table-responsive">
                          <table className="table table-sm tableWrap__table">
                            <thead className="thead-light __appTheadColor">
                              <tr>
                                <th>Course</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {previousRefunds.map((pr) => (
                                <tr key={pr._id}>
                                  <td>{pr.courseName}</td>
                                  <td>₹{Number(pr.amount || 0).toLocaleString('en-IN')}</td>
                                  <td>
                                    <span
                                      className={STATUS_BADGE[pr.status] || 'badge bg-secondary'}
                                      style={{
                                        fontSize: '0.75rem',
                                        textTransform: 'capitalize',
                                      }}
                                    >
                                      {(pr.status || '').replace(/_/g, ' ')}
                                    </span>
                                  </td>
                                  <td>{formatDate(pr.submittedAt)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </InfoCard>
                    )}
                  </div>

                  <div className="col-lg-4">
                    {/* 4. Status Timeline */}
                    <InfoCard title="Status Timeline">
                      <StatusTimeline statusHistory={refund.statusHistory || []} />
                    </InfoCard>

                    {/* 5. Action Panel */}
                    {canAct && (
                      <InfoCard title="Actions">
                        <p
                          className="my-4 text-muted"
                          style={{ fontSize: '1.1rem', lineHeight: '1.6' }}
                        >
                          Approving will <strong>immediately</strong> trigger an automatic refund to
                          the user's original payment method via the payment gateway.
                        </p>
                        <div className="d-flex flex-column align-items-center">
                          <div className="mb-3 w-75">
                            <AppButton
                              variantClass="btn-primary w-100 d-flex align-items-center justify-content-center"
                              style={{
                                background: 'linear-gradient(90deg, #14a38b, #0e7e6b)',
                                border: 'none',
                                height: '55px',
                                fontSize: '1.2rem',
                                fontWeight: 700,
                                minWidth: 'auto',
                              }}
                              onClick={() => setModal({ open: true, mode: 'approve' })}
                              disabled={actionLoading}
                            >
                              <span className="d-flex align-items-center gap-2">
                                Approve &amp; Process
                              </span>
                            </AppButton>
                          </div>
                          <div className="w-75">
                            <AppButton
                              variantClass="btn-primary w-100 d-flex align-items-center justify-content-center"
                              style={{
                                background: 'linear-gradient(90deg, #d32f2f, #b71c1c)',
                                border: 'none',
                                height: '55px',
                                fontSize: '1.2rem',
                                fontWeight: 700,
                                minWidth: 'auto',
                              }}
                              onClick={() => setModal({ open: true, mode: 'reject' })}
                              disabled={actionLoading}
                            >
                              <span className="d-flex align-items-center gap-2">Reject Refund</span>
                            </AppButton>
                          </div>
                        </div>
                      </InfoCard>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default RefundDetailPage;
